/**
 * LA BASE DE SAVOIR D'UN CHAPITRE, dans la page « Programme » — fiches,
 * capsules, erreurs et remèdes, et la publication du chapitre (chantier
 * « manuel vivant », Φ-MV-2, MV-CNT-05, MV-CNT-06).
 *
 * Quatre sections, posées sous celles du programme par `ChapitreProgrammePage`.
 * TOUTES sont repliées à l'arrivée (MV-UX-02 : la page n'ouvre qu'une section,
 * les notions) ; leur résumé dit ce qu'il y a derrière ET ce qui reste à
 * relire — « 7 fiches · 7 non relues ».
 *
 * ─── Ce que cette page montre, que l'élève ne voit pas ──────────────────────
 *
 * Tout ce qui est importé, en service ou non, avec ses deux états : le
 * service (seul « Publié » est servi) et la relecture (« Produit par agent —
 * non relu », ou qui a relu et quand). La lecture de l'élève
 * (`/v1/manuel/…`) ne rend ni l'un ni l'autre.
 *
 * ─── Une ligne mène à une sous-page ─────────────────────────────────────────
 *
 * Relire une fiche est une lecture longue : elle a son adresse
 * (`/programme/:code/fiches/:fiche`), qui s'envoie et à laquelle un retour
 * arrière ramène (`RelectureSavoirPages.tsx`). La liste, elle, ne porte aucun
 * bouton : on relit en lisant, pas en cochant une ligne.
 *
 * ─── Les exercices comptent (refonte de la console, L4) ─────────────────────
 *
 * Le compte « non relus » de la publication ignorait les exercices : « Les
 * exposants » disait « 41 contenus non relus » quand il y en avait 105. Le
 * chapitre lit désormais aussi ses exercices (`GET …/chapitres/:code/exercices`,
 * route existante) : une section « Exercices » dit où en est leur relecture et
 * mène à leur file (`/contenus/:code/exercices`), et la publication les compte —
 * « à corriger » à part (D-1).
 */
import {
  denombrer,
  dureeEnClair,
  type ContentStatus,
  type ExercicesChapitreResponse,
  type SavoirChapitreResponse,
} from '@silex/shared';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import { ActionsSavoir } from '../components/ActionsSavoir';
import { Badge } from '../components/Badge';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { ListePaginee } from '../components/Pagination';
import { SectionRabattable } from '../components/SectionRabattable';
import { TexteMaths } from '../components/TexteMaths';
import { adresseRelectureExercices } from '../lib/contenus-format';
import { statutChapitre } from '../lib/programme-format';
import { progression, resumePublication, resumeExercices } from '../lib/relecture-exercices';
import {
  adresses,
  etatDuRemede,
  etatRelecture,
  nommerNotions,
  resumesDuSavoir,
  statutService,
  totalNonRelus,
} from '../lib/savoir-format';
import { useAsyncRechargeable } from '../lib/useAsync';

export interface SavoirDuChapitreProps {
  readonly chapitre: {
    readonly code: string;
    /** Le rang du chapitre : il numérote ses notions (« 3.2 »). */
    readonly rang: number;
    readonly statut: ContentStatus;
    readonly nbNotions: number;
  };
  /** Le statut du chapitre a changé : au parent de relire son en-tête. */
  readonly surChangementDuChapitre: () => void;
}

export function SavoirDuChapitre({ chapitre, surChangementDuChapitre }: SavoirDuChapitreProps) {
  /* Les exercices sont lus À CÔTÉ, sans retenir le reste : une lecture qui
     échoue les dit inconnus (`null`), elle n'éteint pas les fiches. */
  const loader = useMemo(
    () => async () => {
      const [savoir, exercices] = await Promise.all([
        api.savoirChapitre(chapitre.code),
        api.manuelExercicesChapitre(chapitre.code).catch(() => null),
      ]);
      return { savoir, exercices };
    },
    [chapitre.code],
  );
  const { resource: lu, recharger } = useAsyncRechargeable(loader);

  if (lu.status === 'loading')
    return <Chargement quoi="les exercices, fiches, capsules et erreurs" />;
  if (lu.status === 'error') return <ErreurRessource message={lu.message} />;

  return (
    <Sections
      chapitre={chapitre}
      savoir={lu.data.savoir}
      exercices={lu.data.exercices}
      surChangement={() => {
        recharger();
        surChangementDuChapitre();
      }}
    />
  );
}

function Sections({
  chapitre,
  savoir,
  exercices,
  surChangement,
}: {
  readonly chapitre: SavoirDuChapitreProps['chapitre'];
  readonly savoir: SavoirChapitreResponse;
  /** `null` : la lecture des exercices a échoué — ils ne sont pas comptés, et c'est dit. */
  readonly exercices: ExercicesChapitreResponse | null;
  readonly surChangement: () => void;
}) {
  const resumes = resumesDuSavoir(savoir);
  const cle = (section: string): string => `programme.${chapitre.code}.${section}`;
  const notions = (liste: Parameters<typeof nommerNotions>[0]): string =>
    nommerNotions(liste, chapitre.rang, chapitre.nbNotions);
  const nonRelus = totalNonRelus(savoir);
  const statut = statutChapitre(chapitre.statut);
  const progExercices = exercices === null ? null : progression(exercices.exercices);

  return (
    <>
      {/* ── Les exercices (L4) ─────────────────────────────────────────── */}
      <div data-testid="savoir-exercices">
        <SectionRabattable
          titre="Exercices"
          resume={resumeExercices(progExercices)}
          sous="Tels que l’élève les reçoit, avec leur corrigé. Ils se relisent dans leur file."
          ouverteParDefaut={false}
          cleMemoire={cle('exercices')}
        >
          {progExercices === null ? (
            <p className="note-line">Les exercices n’ont pas pu être lus.</p>
          ) : (
            <div className="actions-savoir__barre">
              <Link className="btn btn--sm" to={adresseRelectureExercices(chapitre.code)}>
                Relire les exercices
              </Link>
              {progExercices.aCorriger > 0 ? (
                <Link
                  className="btn btn--ghost btn--sm"
                  to={`${adresseRelectureExercices(chapitre.code)}?etat=a_corriger`}
                >
                  Voir les {progExercices.aCorriger} à corriger
                </Link>
              ) : null}
            </div>
          )}
        </SectionRabattable>
      </div>

      {/* ── Les fiches ─────────────────────────────────────────────────── */}
      <div data-testid="savoir-fiches">
        <SectionRabattable
          titre="Fiches"
          resume={resumes.fiches}
          sous="Telles que l’élève les lit. Ouvrir une fiche pour la relire."
          ouverteParDefaut={false}
          cleMemoire={cle('fiches')}
        >
          {savoir.fiches.length === 0 ? (
            <p className="note-line">Aucune fiche importée pour ce chapitre.</p>
          ) : (
            <ListePaginee
              elements={savoir.fiches}
              param="p-fiches"
              singulier="fiche"
              testId="savoir-fiches-liste"
            >
              {(page) => (
                <div className="list list--programme">
                  {page.map((f) => {
                    const service = statutService(f.relecture.statut);
                    const relecture = etatRelecture(f.relecture, 'fiches');
                    return (
                      <Link
                        key={f.code}
                        className="li li--link"
                        to={adresses.fiche(chapitre.code, f.code)}
                      >
                        <span className="li-main">
                          <span className="li-title">{f.titre}</span>
                          <span className="li-sub">
                            {f.nature === 'revision' ? 'Fiche de révision · ' : ''}
                            {notions(f.notions)}
                          </span>
                          <span className="li-sub">
                            {f.minutesLecture} min de lecture · {denombrer(f.nbAncres, 'section')} ·
                            version {f.version}
                          </span>
                        </span>
                        <span className="li-etats">
                          <Badge tone={service.tone} label={service.label} />
                          <Badge tone={relecture.tone} label={relecture.label} />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </ListePaginee>
          )}
        </SectionRabattable>
      </div>

      {/* ── Les capsules ───────────────────────────────────────────────── */}
      <div data-testid="savoir-capsules">
        <SectionRabattable
          titre="Capsules"
          resume={resumes.capsules}
          sous="Une vidéo courte, une idée. Ouvrir une capsule pour l’écouter."
          ouverteParDefaut={false}
          cleMemoire={cle('capsules')}
        >
          {savoir.capsules.length === 0 ? (
            <p className="note-line">Aucune capsule importée pour ce chapitre.</p>
          ) : (
            <ListePaginee
              elements={savoir.capsules}
              param="p-capsules"
              singulier="capsule"
              testId="savoir-capsules-liste"
            >
              {(page) => (
                <div className="list list--programme">
                  {page.map((c) => {
                    const service = statutService(c.relecture.statut);
                    const relecture = etatRelecture(c.relecture, 'capsules');
                    return (
                      <Link
                        key={c.code}
                        className="li li--link"
                        to={adresses.capsule(chapitre.code, c.code)}
                      >
                        <span className="li-main">
                          <span className="li-title">{c.titre}</span>
                          <span className="li-sub">{notions(c.notions)}</span>
                          <span className="li-sub">
                            {/* T40 : une capsule mise de côté n'a pas de vidéo. */}
                            {c.etat === 'a_venir' ? 'à venir' : dureeEnClair(c.dureeSec)} · {c.idee}{' '}
                            · version {c.version}
                          </span>
                        </span>
                        <span className="li-etats">
                          {c.etat === 'a_venir' ? <Badge tone="en-cours" label="à venir" /> : null}
                          <Badge tone={service.tone} label={service.label} />
                          <Badge tone={relecture.tone} label={relecture.label} />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </ListePaginee>
          )}
        </SectionRabattable>
      </div>

      {/* ── Les erreurs et leurs remèdes ───────────────────────────────── */}
      <div data-testid="savoir-erreurs">
        <SectionRabattable
          titre="Erreurs et remèdes"
          resume={resumes.erreurs}
          sous="Ce que la correction nomme, et où elle envoie l’élève."
          ouverteParDefaut={false}
          cleMemoire={cle('erreurs')}
        >
          {savoir.erreurs.length === 0 ? (
            <p className="note-line">Aucune erreur importée pour ce chapitre.</p>
          ) : (
            <ListePaginee
              elements={savoir.erreurs}
              param="p-erreurs"
              singulier="erreur"
              testId="savoir-erreurs-liste"
            >
              {(page) => (
                <div className="list list--programme">
                  {page.map((e) => {
                    const remede = etatDuRemede(e);
                    const relecture = etatRelecture(e, 'erreurs');
                    return (
                      <Link
                        key={e.code}
                        className="li li--link"
                        to={adresses.erreur(chapitre.code, e.code)}
                      >
                        <span className="li-main">
                          <span className="li-title">{e.titre}</span>
                          <span className="li-sub">
                            {notions([e.notion])}
                            {e.exemple ? (
                              <>
                                {' · '}
                                <TexteMaths texte={e.exemple} />
                              </>
                            ) : null}
                          </span>
                        </span>
                        <span className="li-etats">
                          <Badge tone={remede.tone} label={remede.label} />
                          <Badge tone={relecture.tone} label={relecture.label} />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </ListePaginee>
          )}
        </SectionRabattable>
      </div>

      {/* ── La publication du chapitre ─────────────────────────────────── */}
      <div data-testid="savoir-publication">
        <SectionRabattable
          titre="Publication"
          resume={resumePublication(statut.label, nonRelus, progExercices)}
          sous="Seul un chapitre publié est servi aux élèves."
          ouverteParDefaut={false}
          cleMemoire={cle('publication')}
        >
          <p className="note-line">
            Le contenu importé est servi dès son import, puis relu : la relecture ne retient pas un
            chapitre. Publier vérifie que rien ne manque — chaque erreur doit mener à un piège d’une
            fiche en service.
          </p>
          {savoir.erreursSansAncre.length > 0 ? (
            <div className="callout callout--calm" data-testid="savoir-erreurs-sans-ancre">
              <strong>
                {denombrer(
                  savoir.erreursSansAncre.length,
                  'erreur sans remède en service',
                  'erreurs sans remède en service',
                )}
                .
              </strong>{' '}
              {savoir.erreurs
                .filter((e) => savoir.erreursSansAncre.includes(e.code))
                .map((e) => `« ${e.titre} »`)
                .join(' ; ')}
            </div>
          ) : null}
          <ActionsSavoir
            cible="chapitres"
            code={chapitre.code}
            statut={chapitre.statut}
            surChangement={surChangement}
          />
        </SectionRabattable>
      </div>
    </>
  );
}
